import React from "react";
import { observer } from "mobx-react";
import { FormPlaceholder } from "../../settingsForms/fullWidthPlaceholder/formPlaceholder";
import { NewItemForm } from "../components/agentStatusNewForm";
import { AgentStatusList } from "../components/agentStatusList";
import classNames from "classnames";
import commonStyles from "../../../../containers/settings/settingsEditCommon.css";
import settingsStyles from "../../../../styles/settings.css";
import { L10n } from "@fidget/common/i18n";
import { intl } from "@fidget/common/i18n/TypedIntl";
import { SubtitleDescription } from "../../subtitleDescription";
import { AgentEditStatusProvider, useAgentEditStatus } from "./useEditStatus";
import { useStore } from "../../../../models";
import { AgentEditStatusForm } from "./types";
import { SettingsListFilterProvider } from "../../settingsForms/filters/useListFilter/useListFilter";
import { withPreloading } from "../../../HOC/withPreloading/withPreloading";
import { TranslationString } from "../types";
import { AppPresence } from "@closerplatform/spinner-openapi";

interface Props {
  onGoBack?: () => void;
}

const AgentEditStatusComponent = observer(({ onGoBack }: Props) => {
  const { settings, status } = useAgentEditStatus();
  const { uiStore } = useStore();
  const { newItem, currentlyEditedItem } = settings;

  const handleGoBack = () => {
    if (newItem?.$ || currentlyEditedItem?.$) {
      uiStore.addErrorToast(intl.formatMessage("settings.widgetConfig.unsavedChanges"));
      return;
    }
    onGoBack?.();
  };

  const renderTitle = () => (
    <div className={classNames(commonStyles.title, settingsStyles.pointer)} onClick={handleGoBack}>
      <i className={classNames("icon-arrow", settingsStyles.rotated)} />
      <L10n id={`settings.adviserStatuses.${status}.title` as TranslationString} />
    </div>
  );

  const renderSubTitle = () => (
    <SubtitleDescription
      subtitle={intl.formatMessage("settings.adviserStatuses.substatuses.title")}
      description={intl.formatMessage(
        `settings.adviserStatuses.${status}.description` as TranslationString
      )}
    />
  );

  const renderAddNewSection = () => {
    return settings.newItem ? (
      <NewItemForm />
    ) : (
      <FormPlaceholder
        onClick={settings.handleCreateNewClick}
        text={
          "+ " +
          intl.formatMessage(`settings.adviserStatuses.${status}.form.open` as TranslationString)
        }
      />
    );
  };

  const renderListSection = () => {
    return <AgentStatusList />;
  };

  return (
    <>
      {renderTitle()}
      <div className={commonStyles.section}>
        {renderSubTitle()}
        <div className={commonStyles.content}>
          {renderAddNewSection()}
          {renderListSection()}
        </div>
      </div>
    </>
  );
});

export type AgentStatusEditProps = Props & {
  status: AppPresence;
};

const AgentStatusEditContainer = (props: AgentStatusEditProps) => (
  <SettingsListFilterProvider<AgentEditStatusForm>>
    <AgentEditStatusProvider status={props.status}>
      <AgentEditStatusComponent {...props} />
    </AgentEditStatusProvider>
  </SettingsListFilterProvider>
);

export const AgentStatusEdit = withPreloading(AgentStatusEditContainer);
